import {useCallback, useMemo, type ReactElement} from 'react'
import {useFragment, useRefetchableFragment} from 'react-relay'
import {graphql} from 'relay-runtime'
import {NestedListItem} from '@github-ui/nested-list-view/NestedListItem'
import {NestedListItemTitle} from '@github-ui/nested-list-view/NestedListItemTitle'
import {NestedListItemMetadata} from '@github-ui/nested-list-view/NestedListItemMetadata'
import {AvatarStack, Box, Link, Octicon} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {NestedListItemLeadingContent} from '@github-ui/nested-list-view/NestedListItemLeadingContent'
import {CommentIcon, PersonIcon, CircleIcon} from '@primer/octicons-react'
import {NestedListItemTrailingBadge} from '@github-ui/nested-list-view/NestedListItemTrailingBadge'
import {NestedListViewCompletionPill} from '@github-ui/nested-list-view/NestedListViewCompletionPill'
import {userHovercardPath} from '@github-ui/paths'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'

import {SubIssuesActionBar} from './SubIssuesActionBar'
import {SubIssueTypeIndicator} from './SubIssueTypeIndicator'
import {getIssueSearchURL} from '../utils/urls'
import {SubIssueStateIcon} from './SubIssueStateIcon'
import type {QUERY_FIELDS} from '../constants/queries'

import type {SubIssuesListItem$key} from './__generated__/SubIssuesListItem.graphql'
import type {SubIssuesListItem_NestedFragment$key} from './__generated__/SubIssuesListItem_NestedFragment.graphql'
import type {SubIssuesListItem_NestedQuery} from './__generated__/SubIssuesListItem_NestedQuery.graphql'

const SubIssuesListItem_NestedFragment = graphql`
  fragment SubIssuesListItem_NestedFragment on Issue
  @refetchable(queryName: "SubIssuesListItem_NestedQuery")
  @argumentDefinitions(fetchSubIssues: {type: "Boolean", defaultValue: false}) {
    subIssues(first: 50) @include(if: $fetchSubIssues) {
      nodes {
        id
        ...SubIssuesListItem
      }
    }
  }
`

const SubIssuesListItemFragment = graphql`
  fragment SubIssuesListItem on Issue {
    id
    totalCommentsCount
    ...SubIssueStateIcon
    assignees(first: 10) {
      totalCount
      edges {
        node {
          id
          login
          avatarUrl
        }
      }
    }
    subIssuesSummary {
      total
      completed
      percentCompleted
    }
    title
    titleHTML
    url
    ...SubIssueTypeIndicator
    number
    repository {
      name
      owner {
        login
      }
    }
    ...SubIssuesListItem_NestedFragment @arguments(fetchSubIssues: false)
  }
`

export function SubIssuesListItem({
  parentIssueId,
  issueKey,
  onSubIssueClick,
  // Temporary prop to control drag-ability of items. Used for M1 when we can only drag first level items
  // Will be removed in M2 when dnd is enabled for all the items.
  dnd = false,
}: {
  parentIssueId: string
  issueKey: SubIssuesListItem$key
  onSubIssueClick?: (itemIdentifier: ItemIdentifier, url: string) => void
  dnd?: boolean
}) {
  const issueNode = useFragment(SubIssuesListItemFragment, issueKey)

  const onIssueClick = useCallback(
    (
      event: React.MouseEvent,
      listItemNode: {
        number: number
        repository: {
          name: string
          owner: {
            login: string
          }
        }
        url: string
      } & unknown,
    ) => {
      // shortcircuit to default link behaviors if using middle click or modifiers
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.button === 1 || event.shiftKey || event.ctrlKey || event.metaKey) return
      // this is for when a sub-issue is clicked within the sidepanel
      if (onSubIssueClick) {
        event.preventDefault()

        const number = listItemNode.number
        const repo = listItemNode.repository.name
        const owner = listItemNode.repository.owner.login
        const url = listItemNode.url

        onSubIssueClick(
          {
            number,
            repo,
            owner,
            type: 'Issue',
          },
          url,
        )
      }
    },
    [onSubIssueClick],
  )

  const getMetadataHref = useCallback(
    ({owner, repo}: {owner: string; repo: string}, type: keyof typeof QUERY_FIELDS, name: string) => {
      return getIssueSearchURL({owner, repo}, type, name)
    },
    [],
  )

  const trailingBadges = useMemo(() => {
    const {total, completed, percentCompleted} = issueNode.subIssuesSummary
    const completionMetadata =
      total > 0 ? (
        <NestedListItemTrailingBadge key={0} title="Sub-issues">
          <NestedListViewCompletionPill
            progress={{
              total,
              completed,
              percentCompleted,
            }}
            href={issueNode.url}
            onClick={e => onIssueClick(e, issueNode)}
          />
        </NestedListItemTrailingBadge>
      ) : null

    if (completionMetadata) return [completionMetadata]
    return undefined
  }, [issueNode, onIssueClick])

  const subItemsCount = issueNode.subIssuesSummary.total
  const [subIssuesData, refetch] = useRefetchableFragment<
    SubIssuesListItem_NestedQuery,
    SubIssuesListItem_NestedFragment$key
  >(SubIssuesListItem_NestedFragment, issueNode)
  const subItems = useMemo(() => {
    const elements = subIssuesData?.subIssues?.nodes?.reduce(
      (arr, subIssueNode) => {
        if (subIssueNode) {
          arr.push(
            <SubIssuesListItem
              key={subIssueNode.id}
              issueKey={subIssueNode}
              parentIssueId={issueNode.id}
              onSubIssueClick={onSubIssueClick}
            />,
          )
        }
        return arr
      },
      [] as Array<ReactElement<typeof NestedListItem>>,
    )
    if (!elements || elements.length === 0) return undefined
    return elements
  }, [issueNode.id, onSubIssueClick, subIssuesData?.subIssues?.nodes])

  if (!issueNode) return null
  return (
    <NestedListItem
      key={issueNode.id}
      itemId={issueNode.id}
      showDragTrigger={dnd}
      title={
        <>
          <NestedListItemTitle
            value={issueNode.titleHTML || issueNode.title}
            href={issueNode.url}
            trailingBadges={trailingBadges}
            onClick={event => onIssueClick(event, issueNode)}
            leadingBadge={
              <SubIssueTypeIndicator
                dataKey={issueNode}
                getIssueTypeHref={name =>
                  getMetadataHref(
                    {owner: issueNode.repository.owner.login, repo: issueNode.repository.name},
                    'type',
                    name,
                  )
                }
              />
            }
          />
        </>
      }
      secondaryActions={
        <SubIssuesActionBar issueId={parentIssueId} subIssueId={issueNode.id} subIssueUrl={issueNode.url} />
      }
      metadata={
        <NestedListItemMetadata sx={{display: 'flex', gap: 2}}>
          <Box sx={{width: 36, display: 'flex', alignItems: 'center', gap: 1}}>
            {issueNode.totalCommentsCount && issueNode.totalCommentsCount > 0 ? (
              <>
                <CommentIcon />
                {issueNode.totalCommentsCount}
              </>
            ) : null}
          </Box>
          <Box sx={{width: 40, display: 'flex'}}>
            {issueNode.assignees.totalCount === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 100,
                  position: 'relative',
                  mr: '-2px',
                }}
              >
                <Octicon icon={PersonIcon} sx={{position: 'absolute', color: 'border.muted'}} />
                <Octicon size={24} icon={CircleIcon} sx={{color: 'border.muted'}} />
              </Box>
            ) : (
              <AvatarStack alignRight>
                {issueNode.assignees.edges?.map(assigneeEdge => {
                  if (!assigneeEdge?.node) return null
                  return (
                    <Link
                      sx={{borderRadius: '100%'}}
                      key={assigneeEdge.node.id}
                      href={getMetadataHref(
                        {owner: issueNode.repository.owner.login, repo: issueNode.repository.name},
                        'assignee',
                        assigneeEdge.node.login,
                      )}
                      data-hovercard-url={userHovercardPath({owner: assigneeEdge.node.login})}
                    >
                      <GitHubAvatar
                        key={assigneeEdge.node.id}
                        alt={assigneeEdge.node.login}
                        src={assigneeEdge.node.avatarUrl}
                      />
                    </Link>
                  )
                })}
              </AvatarStack>
            )}
          </Box>
        </NestedListItemMetadata>
      }
      metadataContainerSx={{flexWrap: ['wrap', 'unset']}}
      subItemsCount={subItemsCount}
      loadSubItems={async () => {
        refetch({fetchSubIssues: true})
      }}
      subItems={subItems}
    >
      <NestedListItemLeadingContent>
        <SubIssueStateIcon dataKey={issueNode} />
      </NestedListItemLeadingContent>
    </NestedListItem>
  )
}

try{ SubIssuesListItem.displayName ||= 'SubIssuesListItem' } catch {}